import { HttpClient } from '@packages/core/http';
import { StorageService } from '@packages/core/storage';
import { type Container, type ContainerOptions, getContainer as getCoreContainer } from '@packages/core/container';
import { AccountsService } from './accounts';
import { AuthService, AuthTokenService } from './auth';
import { BmisService } from './bmis';
import { CidCvnService } from './cid-cvn';
import { CommentsService } from './comments';
import { ContentSettingsService } from './content-settings';
import { CvtService } from './cvt';
import { DashboardService } from './dashboard/dashboard.service';
import { DmsService } from './dms';
import { EntitiesService } from './entities';
import { EscalationService } from './escalations';
import { EvBatteryService } from './ev-battery-service/evbattery.service';
import { FeedbackService, FeedbackAdminService } from './feedback';
import { InvestigationService } from './investigations';
import { LocationsService } from './locations';
import { NotificationsService } from './notifications';
import { PermissionsService } from './permissions';
import { QmrsService, FailCodesService } from './qmrs';
import { QmrsDashboardDistrictStatsService, QmrsDashboardService, PendingReviewQmrsService } from './qmrsdashboard';
import { SynonymsService } from './synonyms';
import { TechlineService } from './techline/techline.service';
import { ToughbookAttachmentsService } from './toughbook-attachments';
import { WorksheetsService } from './worksheets';

let cached: Container | null = null;

/**
 * Register all domain-level service factories in the IoC container.
 * All registrations are lazy; nothing is constructed until first `resolve()`.
 * Platform primitives ({@link HttpClient}, {@link StorageService}) must be
 * registered as instances by the app layer before anything is resolved.
 *
 * @param container - the core container (already has core service factories)
 * @returns the same container, with domain service factories registered
 */
export function registerContextServices(container: Container): Container {
    // Auth
    container.register(AuthService, [HttpClient]);
    container.register(AuthTokenService, [StorageService]);

    // Domain services (all depend on HttpClient)
    container.register(AccountsService, [HttpClient]);
    container.register(BmisService, [HttpClient]);
    container.register(CidCvnService, [HttpClient]);
    container.register(CommentsService, [HttpClient]);
    container.register(ContentSettingsService, [HttpClient]);
    container.register(CvtService, [HttpClient]);
    container.register(DashboardService, [HttpClient]);
    container.register(DmsService, [HttpClient]);
    container.register(EntitiesService, [HttpClient]);
    container.register(EscalationService, [HttpClient]);
    container.register(EvBatteryService, [HttpClient]);
    container.register(FailCodesService, [HttpClient]);
    container.register(FeedbackAdminService, [HttpClient]);
    container.register(FeedbackService, [HttpClient]);
    container.register(InvestigationService, [HttpClient]);
    container.register(LocationsService, [HttpClient]);
    container.register(NotificationsService, [HttpClient]);
    container.register(PendingReviewQmrsService, [HttpClient]);
    container.register(PermissionsService, [HttpClient]);
    container.register(QmrsDashboardDistrictStatsService, [HttpClient]);
    container.register(QmrsDashboardService, [HttpClient]);
    container.register(QmrsService, [HttpClient]);
    container.register(SynonymsService, [HttpClient]);
    container.register(TechlineService, [HttpClient]);
    container.register(ToughbookAttachmentsService, [HttpClient]);
    container.register(WorksheetsService, [HttpClient]);

    return container;
}

/**
 * Get the contexts IoC container, creating it on first call and caching for subsequent calls.
 * Bootstraps core service factories via core's `getContainer`, then registers all domain
 * service factories. Nothing is constructed until first `resolve()` call; platform primitives
 * must be registered as instances by the app layer before that happens.
 *
 * @param options - container retrieval options
 * @returns the container with core and domain service factories registered
 */
export function getContainer(options: ContainerOptions = {}): Container {
    const { useCache = true } = options;

    if (useCache && cached) {
        return cached;
    }

    const coreContainer = getCoreContainer(options);
    const container = registerContextServices(coreContainer);
    cached = container;
    return container;
}
